import { useLayoutEffect, useMemo, useRef } from 'react';
import { ConfiguredLoadingVisual } from '@/components/ui/configured-loading-visual';
import { getBootstrapSplashSnapshot } from '@/lib/branding/bootstrapPublicBranding';
import { traceSplash } from '@/lib/traceLoading';
import { cn } from '@/lib/utils';

/**
 * Branded bootstrap splash — shown while BootstrapRoot awaits public config.
 * Reads branding:v1 cache synchronously (stale-ok); no extra API calls.
 *
 * Repair-14D: preReact splash is released by coordinator when cold-boot transition ends
 * (see preReactBootHandoff.ts) — do not remove on unmount here.
 */
export function BootstrapSplash() {
  const { publicUi } = useMemo(() => getBootstrapSplashSnapshot(), []);
  const hadPreReactBootRef = useRef(
    typeof document !== 'undefined' && !!document.getElementById('pre-react-boot-splash'),
  );

  useLayoutEffect(() => {
    traceSplash(true, 'bootstrap_root');
    return () => {
      traceSplash(false, 'bootstrap_root');
    };
  }, []);

  const animationsEnabled = publicUi.enable_animations;

  if (hadPreReactBootRef.current) {
    return (
      <div data-bootstrap-splash="handoff" className="sr-only" aria-hidden="true" data-pre-react-handoff="true" />
    );
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background',
        !animationsEnabled && 'no-animations',
      )}
      data-bootstrap-splash="true"
      role="status"
      aria-live="polite"
    >
      <ConfiguredLoadingVisual scope="fullscreen" animationsEnabled={animationsEnabled} />
    </div>
  );
}
